import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TableContainer from "@mui/material/TableContainer";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import ArrowUpwardRounded from "@mui/icons-material/ArrowUpwardRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import AddCommentRounded from "@mui/icons-material/AddCommentRounded";
import ReplayRounded from "@mui/icons-material/ReplayRounded";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { API_URL } from "config";

const FORMAT_LABELS = {
  xlsx: "Excel",
  pdf: "PDF",
  word: "Word",
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

function AiDataAssistant() {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [conversationToken, setConversationToken] = useState("");
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const questionInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading]);

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
    Accept: "application/json",
  });

  const copyUserMessage = async (content, index) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageIndex(index);
      window.setTimeout(() => {
        setCopiedMessageIndex((current) => (current === index ? null : current));
      }, 1500);
    } catch {
      setError("The message could not be copied. Please try again.");
    }
  };

  const editUserMessage = (content) => {
    setQuestion(content);
    window.requestAnimationFrame(() => {
      const input = questionInputRef.current;
      if (!input) return;
      input.focus();
      input.setSelectionRange(content.length, content.length);
      input.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const startNewConversation = () => {
    if (loading) return;
    setQuestion("");
    setMessages([]);
    setConversationToken("");
    setError("");
    setCopiedMessageIndex(null);
    window.requestAnimationFrame(() => questionInputRef.current?.focus());
  };

  const downloadExport = async (exportToken, format) => {
    const response = await axios.post(
      `${API_URL}/ai-data-assistant/export`,
      { export_token: exportToken, format },
      {
        headers: headers(),
        responseType: "blob",
        timeout: 90000,
        withCredentials: true,
      }
    );

    const disposition = response.headers["content-disposition"] || "";
    const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const plainName = disposition.match(/filename="?([^";]+)"?/i);
    const filename = encodedName
      ? decodeURIComponent(encodedName[1])
      : plainName?.[1] || `ai-data-result.${format === "word" ? "docx" : format}`;
    const url = window.URL.createObjectURL(response.data);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const ask = async (questionOverride = null) => {
    const sourceQuestion = typeof questionOverride === "string" ? questionOverride : question;
    const cleanQuestion = sourceQuestion.trim();
    if (!cleanQuestion || loading) return;

    setError("");
    setLoading(true);
    if (typeof questionOverride !== "string") {
      setQuestion("");
    }

    const conversationHistory = messages
      .filter(
        (message) =>
          message.status !== "error" &&
          ((message.role === "user" && message.content) ||
            (message.role === "assistant" && message.answer))
      )
      .slice(-12)
      .map((message) => ({
        role: message.role,
        content: message.role === "user" ? message.content : message.answer,
      }));
    setMessages((current) => [...current, { role: "user", content: cleanQuestion }]);

    try {
      const response = await axios.post(
        `${API_URL}/ai-data-assistant/ask`,
        {
          question: cleanQuestion,
          history: conversationHistory,
          conversation_token: conversationToken || undefined,
        },
        {
          headers: headers(),
          timeout: 90000,
          withCredentials: true,
        }
      );

      const result = response.data;
      setMessages((current) => [...current, { role: "assistant", ...result }]);
      if (result.conversation_token) {
        setConversationToken(result.conversation_token);
      }

      if (result.export_token && FORMAT_LABELS[result.requested_format]) {
        try {
          await downloadExport(result.export_token, result.requested_format);
        } catch (downloadError) {
          setError(
            `${FORMAT_LABELS[result.requested_format]} file could not be downloaded. Please ask again.`
          );
        }
      }
    } catch (requestError) {
      const message =
        requestError.response?.data?.message ||
        requestError.message ||
        "The request could not be completed.";
      setError(message);
      setMessages((current) => [
        ...current,
        { role: "assistant", status: "error", answer: message, columns: {}, rows: [] },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      ask();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Card
          sx={{
            p: 0,
            overflow: "hidden",
            minHeight: messages.length > 0 || loading ? "70vh" : "auto",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #e7ebf0",
            boxShadow: "0 8px 30px rgba(15, 23, 42, 0.05)",
          }}
        >
          <SoftBox
            px={{ xs: 2, md: 3 }}
            py={1.5}
            sx={{
              order: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #edf0f4",
              backgroundColor: "#ffffff",
            }}
          >
            <SoftTypography variant="h6" fontWeight="bold">
              AI Data Assistant
            </SoftTypography>
            <Tooltip title="New chat" arrow>
              <span>
                <IconButton
                  size="small"
                  aria-label="Start a new chat"
                  onClick={startNewConversation}
                  disabled={loading || (messages.length === 0 && !conversationToken)}
                  sx={{ color: "#67748e" }}
                >
                  <AddCommentRounded sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          </SoftBox>

          {error && (
            <Alert
              severity="error"
              sx={{ order: 2, mx: { xs: 2, md: 3 }, mt: 2, py: 0.5, borderRadius: 2 }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          <SoftBox
            sx={{
              order: 4,
              minHeight: messages.length > 0 || loading ? "12vh" : 0,
              maxHeight: "calc(100vh - 320px)",
              p: messages.length > 0 || loading ? { xs: 2, md: 3 } : 0,
              overflowY: "auto",
              backgroundColor: "#ffffff",
            }}
          >
            {messages.map((message, index) => (
              <SoftBox
                key={`${message.role}-${index}`}
                mb={3}
                display="flex"
                alignItems="flex-start"
                justifyContent={message.role === "user" ? "flex-end" : "flex-start"}
                gap={1.25}
              >
                {message.role === "assistant" && (
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #12b5cb 0%, #1976d2 100%)",
                      color: "#ffffff",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                    }}
                  >
                    AI
                  </SoftBox>
                )}
                <SoftBox
                  sx={{
                    maxWidth:
                      message.role === "user" ? { xs: "88%", md: "76%" } : "min(100%, 900px)",
                    width: message.role === "assistant" ? "min(100%, 900px)" : "auto",
                    minWidth: 0,
                  }}
                >
                  <SoftTypography
                    variant="caption"
                    color="text"
                    display="block"
                    mb={0.5}
                    textAlign={message.role === "user" ? "right" : "left"}
                  >
                    {message.role === "user" ? "You" : "AI Data Assistant"}
                  </SoftTypography>
                  <SoftBox
                    sx={{
                      px: { xs: 1.75, md: 2.25 },
                      py: 1.5,
                      borderRadius:
                        message.role === "user" ? "18px 18px 4px 18px" : "4px 18px 18px 18px",
                      backgroundColor: message.role === "user" ? "#e8f3ff" : "#f7f7f8",
                      border:
                        message.role === "user"
                          ? "1px solid #d7ebff"
                          : "1px solid #edf0f2",
                    }}
                  >
                    <SoftTypography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ whiteSpace: "pre-wrap" }}
                    >
                      {message.role === "user" ? message.content : message.answer}
                    </SoftTypography>

                    {message.role === "assistant" &&
                      Object.keys(message.columns || {}).length > 0 && (
                        <TableContainer
                          sx={{
                            mt: 1.5,
                            maxHeight: 360,
                            overflowX: "auto",
                            border: "1px solid #dfe5ec",
                            borderRadius: 2,
                            backgroundColor: "#ffffff",
                          }}
                        >
                          <SoftBox
                            sx={{
                              minWidth: Math.max(
                                320,
                                Object.keys(message.columns || {}).length * 180
                              ),
                            }}
                          >
                            <SoftBox
                              sx={{
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(" +
                                  Math.max(1, Object.keys(message.columns || {}).length) +
                                  ", minmax(160px, 1fr))",
                                backgroundColor: "#eef3f8",
                                borderBottom: "1px solid #dfe5ec",
                              }}
                            >
                              {Object.entries(message.columns).map(([key, label], columnIndex) => (
                                <SoftTypography
                                  key={key}
                                  variant="caption"
                                  fontWeight="bold"
                                  sx={{
                                    px: 2,
                                    py: 1.25,
                                    color: "#344767",
                                    whiteSpace: "nowrap",
                                    borderRight:
                                      columnIndex < Object.keys(message.columns).length - 1
                                        ? "1px solid #dfe5ec"
                                        : "none",
                                  }}
                                >
                                  {label}
                                </SoftTypography>
                              ))}
                            </SoftBox>
                            {(message.rows || []).map((row, rowIndex) => (
                              <SoftBox
                                key={rowIndex}
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "repeat(" +
                                    Math.max(1, Object.keys(message.columns || {}).length) +
                                    ", minmax(160px, 1fr))",
                                  backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#fafbfd",
                                  borderBottom:
                                    rowIndex < (message.rows || []).length - 1
                                      ? "1px solid #edf0f4"
                                      : "none",
                                }}
                              >
                                {Object.keys(message.columns).map((key, columnIndex) => (
                                  <SoftTypography
                                    key={key}
                                    variant="body2"
                                    sx={{
                                      px: 2,
                                      py: 1.25,
                                      color: "#344767",
                                      whiteSpace: "normal",
                                      overflowWrap: "anywhere",
                                      borderRight:
                                        columnIndex < Object.keys(message.columns).length - 1
                                          ? "1px solid #edf0f4"
                                          : "none",
                                    }}
                                  >
                                    {displayValue(row[key])}
                                  </SoftTypography>
                                ))}
                              </SoftBox>
                            ))}
                          </SoftBox>
                        </TableContainer>
                      )}
                  </SoftBox>
                  {message.role === "user" && (
                    <SoftBox mt={0.5} display="flex" justifyContent="flex-end" gap={0.25}>
                      <Tooltip
                        title={copiedMessageIndex === index ? "Copied" : "Copy message"}
                        arrow
                      >
                        <IconButton
                          size="small"
                          aria-label="Copy message"
                          onClick={() => copyUserMessage(message.content, index)}
                          sx={{
                            width: 28,
                            height: 28,
                            color: copiedMessageIndex === index ? "#17a673" : "#67748e",
                          }}
                        >
                          <ContentCopyRounded sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit message" arrow>
                        <IconButton
                          size="small"
                          aria-label="Edit message"
                          onClick={() => editUserMessage(message.content)}
                          disabled={loading}
                          sx={{ width: 28, height: 28, color: "#67748e" }}
                        >
                          <EditRounded sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Resend message" arrow>
                        <IconButton
                          size="small"
                          aria-label="Resend message"
                          onClick={() => ask(message.content)}
                          disabled={loading}
                          sx={{ width: 28, height: 28, color: "#67748e" }}
                        >
                          <ReplayRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </SoftBox>
                  )}
                </SoftBox>
              </SoftBox>
            ))}

            {loading && (
              <SoftBox display="flex" alignItems="center" gap={1} p={1}>
                <CircularProgress size={20} />
                <SoftTypography variant="body2" color="text">
                  Reading permitted data...
                </SoftTypography>
              </SoftBox>
            )}
            <div ref={messagesEndRef} />
          </SoftBox>

          <SoftBox
            sx={{
              order: 3,
              position: "sticky",
              top: 0,
              px: { xs: 2, md: 3 },
              pt: 1.5,
              pb: 2,
              borderBottom: "1px solid #edf0f4",
              backgroundColor: "rgba(255, 255, 255, 0.96)",
              backdropFilter: "blur(8px)",
              zIndex: 1,
            }}
          >
            <SoftBox sx={{ maxWidth: 980, mx: "auto" }}>
              <TextField
                fullWidth
                multiline
                inputRef={questionInputRef}
                minRows={1}
                maxRows={6}
                size="small"
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={handleKeyDown}
                inputProps={{ maxLength: 2000, "aria-label": "Ask the AI Data Assistant" }}
                placeholder="Message AI Data Assistant..."
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ alignSelf: "flex-end", mb: 0.25 }}>
                      <IconButton
                        aria-label="Send question"
                        onClick={() => ask()}
                        disabled={loading || !question.trim()}
                        sx={{
                          width: 34,
                          height: 34,
                          color: "#ffffff",
                          backgroundColor: "#1976d2",
                          "&:hover": { backgroundColor: "#125ea8" },
                          "&.Mui-disabled": { color: "#ffffff", backgroundColor: "#b8c4d1" },
                        }}
                      >
                        {loading ? <CircularProgress size={17} color="inherit" /> : <ArrowUpwardRounded />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    display: "flex",
                    alignItems: "flex-end",
                    borderRadius: "16px",
                    backgroundColor: "#ffffff",
                    px: 1.5,
                    py: 0.75,
                    boxShadow: "0 2px 10px rgba(15, 23, 42, 0.06)",
                  },
                  "& .MuiInputBase-inputMultiline": {
                    flex: "1 1 auto",
                    width: "100%",
                    minWidth: 0,
                    maxHeight: "150px",
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  },
                  "& .MuiOutlinedInput-input": {
                    width: "100%",
                    fontSize: "0.95rem",
                    lineHeight: 1.5,
                    py: 0.5,
                  },
                  "& .MuiInputAdornment-root": {
                    flexShrink: 0,
                    marginLeft: 1,
                  },
                }}
              />
              <SoftTypography variant="caption" color="text" display="block" textAlign="center" mt={0.75}>
                Ctrl + Enter to send · Read-only access
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AiDataAssistant;
